import { Injectable, signal, TemplateRef, computed, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SlideOverPanelConfig } from './slide-over-panel.types';

const EXIT_ANIMATION_MS = 280;

const DEFAULT_CONFIG: SlideOverPanelConfig = {
  title: '',
  initialWidth: 560,
  minWidth: 420,
  maxWidthVw: 80
};

/**
 * Service to control the slide-over panel from any component.
 *
 * Usage:
 * ```ts
 * panelService.open(
 *   { title: 'My Asset', subtitle: 'asset-id-123', status: { label: 'Aktiv', type: 'success' } },
 *   { bodyTemplate: this.detailTpl, footerTemplate: this.footerTpl, context: myData }
 * );
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SlideOverPanelService {
  private readonly doc = inject(DOCUMENT);
  private readonly _isOpen = signal(false);
  private readonly _closing = signal(false);
  private readonly _config = signal<SlideOverPanelConfig>(DEFAULT_CONFIG);
  private readonly _bodyTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly _footerTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly _headerActionsTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly _templateContext = signal<unknown>(null);

  readonly isOpen = this._isOpen.asReadonly();
  readonly closing = this._closing.asReadonly();
  readonly config = this._config.asReadonly();
  readonly bodyTemplate = this._bodyTemplate.asReadonly();
  readonly footerTemplate = this._footerTemplate.asReadonly();
  readonly headerActionsTemplate = this._headerActionsTemplate.asReadonly();
  readonly templateContext = this._templateContext.asReadonly();

  /** Whether the panel DOM should be rendered (open or animating out) */
  readonly isVisible = computed(() => this._isOpen() || this._closing());

  open(config: SlideOverPanelConfig, options?: {
    bodyTemplate?: TemplateRef<unknown>;
    footerTemplate?: TemplateRef<unknown>;
    headerActionsTemplate?: TemplateRef<unknown>;
    context?: unknown;
  }): void {
    this._config.set({ ...DEFAULT_CONFIG, ...config });
    this._bodyTemplate.set(options?.bodyTemplate ?? null);
    this._footerTemplate.set(options?.footerTemplate ?? null);
    this._headerActionsTemplate.set(options?.headerActionsTemplate ?? null);
    this._templateContext.set(options?.context ?? null);
    this._closing.set(false);
    this._isOpen.set(true);
    this.doc.body.style.overflow = 'hidden';
  }

  close(): void {
    if (!this._isOpen()) return;
    this._closing.set(true);
    this._isOpen.set(false);

    setTimeout(() => {
      this._closing.set(false);
      this._bodyTemplate.set(null);
      this._footerTemplate.set(null);
      this._headerActionsTemplate.set(null);
      this._templateContext.set(null);
      this._config.set(DEFAULT_CONFIG);
      this.doc.body.style.overflow = '';
    }, EXIT_ANIMATION_MS);
  }

  /** Update config without closing (e.g. title after async load) */
  patchConfig(patch: Partial<SlideOverPanelConfig>): void {
    this._config.update(c => ({ ...c, ...patch }));
  }

  /** Replace template context data (e.g. after a data refresh) */
  updateContext(context: unknown): void {
    this._templateContext.set(context);
  }
}
